import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import SaveAsIcon from "@mui/icons-material/SaveAs";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import { setProperty } from "dot-prop";
import isEqual from "lodash.isequal";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ErrorDialog from "../../../components/dialogs/ErrorDialog.js";
import EditableAutocomplete from "../../../components/EditableAutocomplete.js";
import EditableField from "../../../components/EditableField.js";
import EditButton from "../../../components/EditButton.js";
import LoadingBackdrop from "../../../components/LoadingBackdrop.js";
import Event from "../../../components/page-item/Event.js";
import SectionTitle from "../../../components/page-item/SectionTitle.js";
import { useUser } from "../../../components/UserContext.js";
import {
  getCharacters,
  getEmployees,
  getPlot,
  postPlot,
  putPlot,
} from "../../../lib/apiConfig.js";
import {
  PAGE_CONTAINER_SX,
  PAGE_TITLE_SX,
  ROUTES,
} from "../../../lib/constants.js";
import { filterVisibleCharacters } from "../../../lib/filterVisibleCharacters.js";
import { formatString } from "../../../lib/formatString.js";
import { useFetch } from "../../../lib/hooks/useFetch.js";
import PlotInvolvements from "./PlotInvolvements.js";

const emptyPlot = {
  name: null,
  description: null,
  eventName: null,
  eventTime: null,
  eventLocation: null,
  writer: null,
  charactersInvolved: [],
};

export default function PlotPage({ isCreating, locales }) {
  const { canEdit, canViewOtherHidden, hostEmployeeId, runId } = useUser();
  const { state } = useLocation();
  const navigate = useNavigate();
  const [plotId, setPlotId] = useState(null);
  const [originalPlot, setOriginalPlot] = useState(null);
  const [plot, setPlot] = useState(isCreating ? emptyPlot : null);
  const [employees, setEmployees] = useState(null);
  const [characters, setCharacters] = useState(null);
  const [isEditing, setIsEditing] = useState(isCreating);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const setError = (error) => {
    setErrorMessage(error.message);
    setShowError(true);
  };

  const { isFetching: isFetchingPlot } = useFetch({
    endpoint: getPlot({ id: plotId, runId }),
    mustFetch: plotId && !plot,
    onSuccess: (response) => {
      setOriginalPlot(response);
      setPlot(response);
    },
    onFail: setError,
  });

  const { fetchData: fetchEmployees, isFetching: isFetchingEmployees } =
    useFetch({
      endpoint: getEmployees({ runId }),
      mustFetch: isCreating && !employees,
      onSuccess: setEmployees,
      onFail: setError,
    });

  const { fetchData: fetchCharacters, isFetching: isFetchingCharacters } =
    useFetch({
      endpoint: getCharacters({ runId }),
      mustFetch: isCreating && !characters,
      onSuccess: (response) => {
        const processedVisibleCharacters = filterVisibleCharacters({
          canViewOtherHidden,
          characters: response,
          hostEmployeeId,
        }).map((character) => ({
          id: character.id,
          name: character.name,
        }));

        setCharacters(processedVisibleCharacters);
      },
      onFail: setError,
    });

  const { fetchData: savePlot, isFetching: isSavingPlot } = useFetch({
    endpoint: putPlot({ id: plotId, runId }),
    onSuccess: (response) => {
      setOriginalPlot(response);
      setPlot(response);
      setIsEditing(false);
    },
    onFail: setError,
  });

  const { fetchData: createPlot, isFetching: isCreatingPlot } = useFetch({
    endpoint: postPlot({ runId }),
    onSuccess: (response) => {
      const newPlot = {
        ...response,
        charactersInvolved: response.charactersInvolved || [],
      };
      const newPlotId = response.id;

      setOriginalPlot(newPlot);
      setPlot(newPlot);
      setIsEditing(false);
      navigate(
        `/${ROUTES.PLOTS.PLOTS.BASE}/${ROUTES.PLOTS.PLOTS.DETAIL.replace(
          ":id",
          newPlotId
        )}`,
        { state: { id: newPlotId } }
      );
    },
    onFail: setError,
  });

  useEffect(() => {
    !isCreating && setPlotId(state.id);
  }, [isCreating, state]);

  const isFilledEvent = useMemo(
    () => plot?.eventName && plot?.eventTime && plot?.eventLocation,
    [plot]
  );

  const handleEditClick = async () => {
    if (isEditing && !isCreating) {
      const newPlot = {
        ...plot,
        eventLocation: isFilledEvent ? plot.eventLocation : "",
        eventName: isFilledEvent ? plot.eventName : "",
        eventTime: isFilledEvent ? plot.eventTime : "",
        charactersInvolved: plot.charactersInvolved.filter(
          (involvement) =>
            Boolean(involvement?.involvement?.trim()) &&
            Boolean(involvement?.character?.id)
        ),
      };

      isEqual(newPlot, originalPlot) ? setIsEditing(false) : savePlot(newPlot);
    } else {
      await Promise.all([
        ...(employees ? [] : [fetchEmployees()]),
        ...(characters ? [] : [fetchCharacters()]),
      ]);
      setIsEditing(true);
    }

    if (isCreating) {
      !isEqual(plot, emptyPlot) && createPlot(plot);
    }
  };

  const changePlotProp = ({ field, value }) => {
    setPlot((prevPlot) => {
      const newPlot = { ...prevPlot };
      setProperty(newPlot, field, value);

      return newPlot;
    });
  };

  const changeCharacterInvolvement = ({ field, index, value }) => {
    setPlot({
      ...plot,
      charactersInvolved: plot.charactersInvolved.map((involvement, i) =>
        i === index ? { ...involvement, [field]: value } : involvement
      ),
    });
  };

  const deleteCharacterInvolvement = (index) => {
    setPlot({
      ...plot,
      charactersInvolved: plot.charactersInvolved.filter(
        (involvement, i) => i !== index
      ),
    });
  };

  const addCharacterInvolvement = () => {
    setPlot({
      ...plot,
      charactersInvolved: [
        ...plot.charactersInvolved,
        { involvement: null, character: {} },
      ],
    });
  };

  return (!isCreating && !plot) ||
    isFetchingPlot ||
    isFetchingEmployees ||
    isFetchingCharacters ||
    isSavingPlot ||
    isCreatingPlot ? (
    <LoadingBackdrop />
  ) : (
    <Container disableGutters sx={PAGE_CONTAINER_SX}>
      <Box
        id="plot-header"
        display="flex"
        alignItems="start"
        justifyContent="space-between"
      >
        <Box display="flex" flexDirection="column">
          <EditableField
            component="h1"
            isEditing={isEditing}
            label={locales["plot.title"]}
            onChange={({ target }) =>
              changePlotProp({ field: "name", value: target.value })
            }
            sx={PAGE_TITLE_SX}
            text={plot.name}
            value={plot.name}
          />
          <EditableAutocomplete
            disabledOptions={(option) => plot.writer?.id === option.id}
            id="plot-writer"
            isEditing={isEditing}
            label={locales["plot.writer.edit"]}
            onChange={(ev, value) =>
              changePlotProp({ field: "writer", value: value })
            }
            optionLabel={(option) => option.name || ""}
            options={employees || []}
            route={
              plot.writer?.id ? `/security/employees/${plot.writer?.id}` : null
            }
            state={{ id: plot.writer?.id }}
            text={
              plot.writer?.name
                ? formatString(locales["plot.writer.noEdit"], [
                    plot.writer?.name,
                  ])
                : locales["plot.writer.unassigned"]
            }
            value={
              employees?.find((option) => option.id === plot.writer?.id) || {}
            }
          />
        </Box>
        {canEdit && (
          <EditButton
            ariaLabel={locales["plot.edit"]}
            isEditing={isEditing}
            onClick={handleEditClick}
            primaryIcon={<EditIcon />}
            secondaryIcon={<SaveAsIcon />}
          />
        )}
      </Box>
      <EditableField
        id="plot-description"
        isEditing={isEditing}
        label={locales["plot.description"]}
        multiline
        onChange={({ target }) =>
          changePlotProp({ field: "description", value: target.value })
        }
        text={plot.description}
        value={plot.description}
        variant="body1"
      />
      {(isFilledEvent || isEditing) && (
        <Box id="plot-event">
          <SectionTitle text={locales["plot.event"]} />
          <Box display="flex" alignItems="start" justifyContent="space-between">
            <Event
              isEditing={isEditing}
              locales={locales}
              onChange={changePlotProp}
              plot={plot}
            />
            {isFilledEvent && isEditing && (
              <EditButton
                ariaLabel={locales["plot.event.delete"]}
                isEditing={isEditing}
                onClick={() => {
                  changePlotProp({ field: "eventName", value: null });
                  changePlotProp({ field: "eventTime", value: null });
                  changePlotProp({ field: "eventLocation", value: null });
                }}
                primaryIcon={<DeleteOutlineIcon />}
              />
            )}
          </Box>
        </Box>
      )}
      {plot.charactersInvolved.length || isEditing ? (
        <Box id="plot-characters">
          <SectionTitle text={locales["plot.characters"]} />
          <PlotInvolvements
            buttonAriaLabel={locales["character.plot.involvement.delete"]}
            involvements={plot.charactersInvolved}
            handleChange={changeCharacterInvolvement}
            handleDelete={deleteCharacterInvolvement}
            isEditing={isEditing}
            predictiveList={characters}
            characterNameLabel={locales["plot.characters.name"]}
            involvementLabel={locales["plot.characters.involvement"]}
          />
          {(isCreating || isEditing) && (
            <EditButton
              ariaLabel={locales["plot.characters.involvement.add"]}
              isEditing={isEditing}
              onClick={addCharacterInvolvement}
              primaryIcon={<PersonAddAlt1Icon />}
            />
          )}
        </Box>
      ) : null}
      <ErrorDialog
        locales={locales}
        message={errorMessage}
        onClose={() => {
          setShowError(false);
          setErrorMessage(null);
        }}
        show={showError}
      />
    </Container>
  );
}
